import { useEffect, useState } from "react";
import env from "../../config/env";
import { useUserInfo } from "../../context/user";
import AgentCard from "./components/AgentCard";
import FilterNav from "./components/FilterNav";
import BuildAgentButton from "./components/CreateModal";
import IntegrationModal from "./components/IntegrationModal";
import { useUsersContext } from "../../context/usersContext";
import useDebouncedEffect from "../../hook/debounce";
import { MINE, matchesCategory } from "./categoryFilter";

const YourAIAgents = () => {
  const userActiveLocation = useUserInfo()[4];
  const BASE_URL = env.API_URL + "/v1";
  const userInfo = useUserInfo()[0];
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [creatorFilter, setCreatorFilter] = useState("");
  // plugins is optional: locations other than the active one arrive from
  // getchatuser without it, and a workspace switch can make one active.
  // Recalculated when the location changes (it can also arrive after mount,
  // or be healed with a full copy by getUser after a switch).
  const [pluginsData, setPluginsData] = useState(
    userActiveLocation?.agenct_config?.plugins?.filter(
      (data) => data?.is_active_for_account
    ) || []
  );
  useEffect(() => {
    setPluginsData(
      userActiveLocation?.agenct_config?.plugins?.filter(
        (data) => data?.is_active_for_account
      ) || []
    );
  }, [userActiveLocation]);
  const [pluginFilter, setPluginFilter] = useState("");
  const [activeOnly, setActiveOnly] = useState(true); // default: true
  const [selectTags, setSelectTags] = useState([]);
  // null = All AI Agents, MINE = uncategorised, otherwise the agency category
  // object itself (see categoryFilter.js for why the object, not just the id).
  const [categoryFilter, setCategoryFilter] = useState(null);
  const { agents, fetchAgentsArray, agentPageLoading, products, agency } = useUsersContext();
  const activeProduct = products?.filter((item) => item.activePlan)?.[0];

  const {
    SSO,
    showToast,
    companyInfo,
    getLocationUsers,
    locationUsers,
    setOnFromPrompt,
    ChatSubmit,
    setUserAsUnread,
    addNewMessage,
    refresh,
  } = useUsersContext();

  // const loadPluginData = async () => {
  //   const { response } = await Service.assistant.GetFunctions();

  //   if (response) {
  //     setPluginsData(response?.data?.data);
  //   }
  // };

  // useEffect(() => {
  //   loadPluginData();
  // }, []);

  const handleRefresh = () => {
    fetchAgentsArray();
  };

  useDebouncedEffect(
    () => {
      handleRefresh();
    },
    [userActiveLocation],
    300
  );

  const pillClass = (active) =>
    `h-8 px-3 rounded-full text-sm font-medium border transition-colors ${
      active
        ? "bg-foreground text-background border-foreground"
        : "bg-background text-foreground border-border hover:border-foreground/40"
    }`;

  const getFilteredAgents = () => {
    let filtered = [...agents];

    // Filter by search
    if (searchQuery.trim() !== "") {
      filtered = filtered.filter(
        (agent) =>
          agent.assistant_name
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (agent.function_names &&
            agent.function_names.some(
              (fn) =>
                typeof fn === "string" &&
                fn.toLowerCase().includes(searchQuery.toLowerCase())
            ))
      );
    }

    // Filter by Creator (assist_type)
    if (creatorFilter) {
      filtered = filtered.filter(
        (agent) => agent.assist_type === creatorFilter
      );
    }

    // Sort
    if (sortOrder === "asc") {
      filtered.sort((a, b) => a.assistant_name.localeCompare(b.assistant_name));
    } else if (sortOrder === "desc") {
      filtered.sort((a, b) => b.assistant_name.localeCompare(a.assistant_name));
    } else if (sortOrder === "createdAt") {
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortOrder === "updatedAt") {
      filtered.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else {
      filtered.sort((a, b) => {
        if (a.pin === b.pin) return 0;
        return a.pin ? -1 : 1;
      });
    }

    if (pluginFilter) {
      const plugins = pluginsData.filter((item) =>
        pluginFilter.some((plugin) =>
          item?.name?.toLowerCase()?.includes(plugin.toLowerCase())
        )
      );

      filtered = filtered.filter((agent) =>
        plugins.some((plugin) => agent.function_names?.includes(plugin?.name))
      );
    }

    if (activeOnly) {
      filtered = filtered.filter((agent) => agent.status === true);
    } else {
      filtered = filtered.filter((agent) => agent.status === false);
    }

    if (selectTags.length > 0) {
      filtered = filtered.filter((agent) => {
        const agentTags = agent?.subCategory || [];
        // Extract IDs from subCategory objects
        const agentTagIds = agentTags.map((tagObj) => String(tagObj.id));
        return selectTags.some((tag) => agentTagIds.includes(String(tag)));
      });
    }

    filtered = filtered.filter((agent) =>
      matchesCategory(agent, categoryFilter)
    );

    return filtered;
  };

  const filteredAgents = getFilteredAgents();

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="page-header">
        <span className="page-eyebrow">Agents</span>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <h1 className="page-display text-3xl md:text-4xl">
            The Agents behind your <span className="accent">Tasks</span>
          </h1>
          <div className="shrink-0">
            <BuildAgentButton
              products={products}
              activeProduct={activeProduct}
              pluginsdata={pluginsData}
              numberOfAgents={agents.length}
              handleRefresh={handleRefresh}
            />
          </div>
        </div>
      </div>

      <div className="mb-6">
        <FilterNav
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          activeOnly={activeOnly}
          setActiveOnly={setActiveOnly}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setCategoryFilter(null)}
          className={pillClass(categoryFilter === null)}
        >
          All AI Agents
        </button>
        <button
          type="button"
          onClick={() =>
            setCategoryFilter(categoryFilter === MINE ? null : MINE)
          }
          className={pillClass(categoryFilter === MINE)}
        >
          Your AI Agents
        </button>
        {agency?.Categories?.map((category, index) => {
          const active =
            categoryFilter !== null &&
            categoryFilter !== MINE &&
            categoryFilter.name === category.name;
          return (
            <button
              key={category.id || category.name || index}
              type="button"
              onClick={() => setCategoryFilter(active ? null : category)}
              className={pillClass(active)}
            >
              {category.name}
            </button>
          );
        })}
      </div>

      {agentPageLoading && agents.length === 0 ? (
        // Skeleton grid on FIRST load only — once agents exist, refetches keep
        // the current cards on screen instead of flashing blank shells
        // (CU-86cark1f1). Placeholder blocks make it read as loading, not as
        // broken empty cards.
        <div className="grid gap-4 xl:gap-5 grid-cols-[repeat(auto-fill,minmax(min(100%,300px),1fr))]">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              aria-hidden="true"
              className="h-[520px] rounded-[15px] border border-border bg-card px-5 pt-6 pb-9 flex flex-col animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="h-6 w-20 rounded-full bg-muted" />
                <div className="h-6 w-16 rounded-md bg-muted" />
              </div>
              <div className="h-6 w-3/4 rounded bg-muted mt-5" />
              <div className="h-3.5 w-full rounded bg-muted mt-4" />
              <div className="h-3.5 w-5/6 rounded bg-muted mt-2" />
              <div className="h-3.5 w-2/3 rounded bg-muted mt-2" />
              <div className="mt-auto">
                <div className="h-10 w-full rounded-md bg-muted" />
                <div className="h-3 w-1/2 rounded bg-muted mt-4" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredAgents.length === 0 ? (
        <div className="empty-state mt-2">
          <p className="empty-title">No agents found</p>
          <p className="empty-lede">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="grid gap-4 xl:gap-5 grid-cols-[repeat(auto-fill,minmax(min(100%,300px),1fr))]">
          {filteredAgents.map((agent, index) => (
            <AgentCard
              key={agent.id || index}
              data={agent}
              handleRefresh={handleRefresh}
              companyInfo={companyInfo}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default YourAIAgents;
